/**
 * 정시 학과 AI 해석 API — 057(`/analysis/jeongsi` 학과 상세 블록) 도메인 함수.
 *
 * 글로벌 에러 처리는 http.ts 인터셉터 1회로 일원화 — 호출부 catch 에서 중복 알림 금지.
 *
 * 연관 계약(specs/057-jeongsi-dept-ai-opinion/contracts/fo-jeongsi-dept-opinion-api.md):
 *   POST /api/fo/analysis/jeongsi/departments/{rowId}/opinion   (§1 생성·재생성)
 *   GET  /api/fo/analysis/jeongsi/departments/{rowId}/opinion   (§2 저장분 조회, 없으면 204)
 *   GET  /api/fo/analysis/jeongsi/opinion/availability          (§3 가용성 — 적립 동기화 지점)
 */
import type { SilentRequestConfig } from '#/api/http';
import { http } from '#/api/http';

/**
 * 생성 요청 타임아웃(ms).
 *
 * AI 호출이 동기·수십 초라 http.ts 기본값(10s)으로는 반드시 끊긴다. **이 요청에만** 얹고
 * 기본값은 건드리지 않는다 — 다른 조회가 이만큼 매달려 있게 만들면 안 된다(045 선례).
 *
 * 🔴 **반드시 백엔드 아웃바운드 타임아웃보다 길어야 한다.** 클라이언트가 먼저 포기하면
 * 서버는 저장·과금을 끝냈는데 화면만 실패로 보인다 — 사용자에게는 "실패했는데 횟수가
 * 줄었다"가 된다(2026-08-30 실제 재현: 프론트 130s < 서버 140s, opinionId=2 는 정상
 * 저장됐는데 화면은 "네트워크 실패").
 *
 * 현행 모델(gemini-3.7-flash) 실측은 **16초**라 이 값은 충분히 여유롭다. 그럼에도 240s 를
 * 유지하는 이유는 BO 에서 모델을 느린 계열로 바꿀 수 있기 때문이다(FR-024) — 모델 교체가
 * 프론트 타임아웃 조정을 요구하면 안 된다. 대기 UX 는 이 상한이 아니라 실측(수십 초)에
 * 맞춰 안내한다.
 */
const CREATE_TIMEOUT_MS = 240_000;

/** 근거 항목 — 서버 계약상 섹션마다 1개 이상 존재한다(빈 배열 분기를 두지 않는 이유). */
export interface OpinionEvidence {
  label: string;
  value: string;
}

/** 서술 섹션. `body` 는 `\n` 문단 + `**굵게**` 두 토큰만 쓰는 경량 마크업이다. */
export interface OpinionSection {
  title: string;
  body: string;
  evidence: OpinionEvidence[];
}

/**
 * 해석의 생성 기준.
 *
 * ⚠️ `scoreChanged`·`formulaChanged` 는 **본문을 가리는 조건이 아니다** — 불일치여도 본문 전체가 내려오고,
 *    화면은 "이전 기준" 배지로만 알린 뒤 재생성을 권한다(FR-014).
 */
export interface OpinionBasis {
  scoreChanged: boolean;
  formulaChanged: boolean;
  /** 생성 시각(ISO-8601+offset). 표시는 formatKst 경유. */
  generatedAt: string;
}

export interface JeongsiDeptOpinion {
  opinionId: number;
  summary: string;
  sections: OpinionSection[];
  basis: OpinionBasis;
  /** 잔여 횟수. 쿼터 비활성(무제한)이면 null — 화면은 잔여 표기를 숨긴다. */
  creditBalance: number | null;
}

export interface OpinionAvailability {
  /** false = 무제한(개발·검증). 화면은 잔여 표기를 숨긴다. */
  quotaEnabled: boolean;
  /** 잔여 횟수. quotaEnabled=false 면 null. */
  creditBalance: number | null;
  /** 성적 저장 여부 — 생성 버튼 활성 판정(FR-010). */
  scoreReady: boolean;
  /**
   * 본인 후기가 없고, 후기를 쓰면 **새로 적립될** 무료 전용 대학이 있음(073 contracts §1).
   * 잔액과 무관한 사실 — 표시 조건은 화면이 `creditBalance <= 0` 과 AND 한다. quotaEnabled=false 면 false.
   */
  reviewRequired: boolean;
  /** 현재 정책값 — 자료 보유 대학 1곳당 적립 횟수(서버 공통코드, 폴백은 서버만 안다). 카피 "대학 1곳당 M회"의 M. */
  grantPerUniversity: number;
}

/**
 * 해석 생성(동기) — 완료까지 수십 초 대기한다.
 *
 * **재생성도 이 함수다**(FR-015) — 저장분이 있어도 새로 만들고 쿼터를 1 차감한다.
 *
 * 402(잔여 소진)는 `silentStatuses` 에 넣는다 — 정상 흐름이고 화면이 잠금 UI 로 표현하므로
 * 전역 토스트가 뜨면 안 된다. 403(미보유)도 같은 이유로 억제한다(040 선례).
 */
export async function createJeongsiDeptOpinion(rowId: number): Promise<JeongsiDeptOpinion> {
  const { data } = await http.post<JeongsiDeptOpinion>(
    `/fo/analysis/jeongsi/departments/${rowId}/opinion`,
    null,
    { timeout: CREATE_TIMEOUT_MS, silentStatuses: [402, 403] } as SilentRequestConfig,
  );
  return data;
}

/**
 * 저장된 해석 조회 — 없으면 `null`(서버 204).
 *
 * 204 는 본문이 없어 axios 가 빈 문자열을 준다 — "아직 만들지 않음"이라는 정상 상태이므로
 * null 로 흡수해 화면이 생성 버튼을 그리게 한다.
 */
export async function fetchJeongsiDeptOpinion(rowId: number): Promise<JeongsiDeptOpinion | null> {
  const response = await http.get<JeongsiDeptOpinion | ''>(
    `/fo/analysis/jeongsi/departments/${rowId}/opinion`,
    { silentStatuses: [403] } as SilentRequestConfig,
  );
  return response.status === 204 || !response.data ? null : (response.data as JeongsiDeptOpinion);
}

/** 가용성 조회 — 이 호출이 서버 측 적립 동기화 지점이다(잔액이 여기서 최신화된다). */
export async function fetchOpinionAvailability(): Promise<OpinionAvailability> {
  const { data } = await http.get<OpinionAvailability>('/fo/analysis/jeongsi/opinion/availability');
  return data;
}

// ── 보관함 단건 재열람(065 WS-14) ──────────────────────────────────────

/**
 * 해석 단건 재열람 응답 — 헤더 + 당시 성적 + 본문(기존 `JeongsiDeptOpinion` 그대로 중첩).
 *
 * `inputSnapshot` 은 036 진단 스냅샷 호환 `{score:{…}}` 라 `DiagnosisInputStrip(scope="JEONGSI")` 를
 * 그대로 쓴다. `opinion.creditBalance` 는 재열람에서 항상 null(쿼터 무관).
 */
export interface JeongsiDeptOpinionDetail {
  /** 대학 id — 정시 분석 화면 필터 딥링크(지금 판정 보기)용. */
  universityId: number;
  universityName: string;
  department: string;
  track: string | null;
  /** 생성 시각(ISO-8601+offset). 표시는 formatKst 경유. */
  createdAt: string;
  inputSnapshot: Record<string, unknown>;
  opinion: JeongsiDeptOpinion;
}

/**
 * 해석 단건 — 보관함에서 id 로 연다. 소유자 아님·미존재 동일 404(페이지가 "찾을 수 없음"으로 그린다).
 *
 * 계약: GET /api/fo/analysis/jeongsi/opinions/{id}(specs/065-report-archive-hub/contracts §2-1).
 * 재열람은 재생성·크레딧 차감이 없다.
 */
export async function fetchJeongsiDeptOpinionDetail(id: number): Promise<JeongsiDeptOpinionDetail> {
  const { data } = await http.get<JeongsiDeptOpinionDetail>(`/fo/analysis/jeongsi/opinions/${id}`, {
    silentStatuses: [404],
  } as SilentRequestConfig);
  return data;
}
